import { FragmentElement } from "./fragment.js";

enum BoostModes {
    FRAGMENT = "fragment",
    PAGE = "page",
    NONE = "none",
}
const boostAttrName = "bd-boost";
const boostIncludeAttrName = "bd-boost-include"; // include inputs in the mutation request, comma separated list of selectors

const eventBeforeFetch = "bd-boost:before-fetch";
function customEventBeforeFetch(url: string) {
    return new CustomEvent(eventBeforeFetch, {
        bubbles: true,
        detail: {
            url,
        },
    });
}
const eventMissing = "bd-boost:missing";
function customEventMissing(fragment: FragmentElement) {
    return new CustomEvent(eventMissing, {
        bubbles: true,
        detail: {
            fragment,
        },
    });
}
const eventAfterSwap = "bd-boost:after-swap";
function customEventAfterSwap(mode: BoostModes) {
    return new CustomEvent(eventAfterSwap, {
        bubbles: true,
        detail: {
            mode,
        },
    });
}
const eventError = "bd-boost:error";
function customEventError(error: any) {
    return new CustomEvent(eventError, {
        bubbles: true,
        detail: {
            error,
        },
    });
}

const requestClassName = "bd-boost-request";
const requestInterval = 100; // in ms

document.addEventListener("DOMContentLoaded", () => {
    document.addEventListener("click", (e: PointerEvent) => {
        const link = (e.target as HTMLElement).closest("a");
        if (!link) {
            return;
        }
        // Ignore clicks with modifier keys (let them open in new tabs normally)
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) {
            return;
        }

        // Ignore cross-origin links
        if (link.origin !== window.location.origin) {
            return;
        }

        const boostIncludeSelectors = link.getAttribute(boostIncludeAttrName);
        if (boostIncludeSelectors) {
            link.href = includeElementsInURL(boostIncludeSelectors, link.href);
        }

        let boostAttr = link.getAttribute(boostAttrName);
        if (boostAttr === "") {
            boostAttr = BoostModes.PAGE;
        }

        if (boostAttr === BoostModes.FRAGMENT) {
            e.preventDefault();
            fragmentNavigate(link, link.href);
            return;
        }

        if (boostAttr === BoostModes.PAGE) {
            e.preventDefault();
            pageNavigate(link.href);
            return;
        }
    });

    document.addEventListener("submit", (e: SubmitEvent) => {
        const form = (e.target as HTMLElement).closest("form");
        if (!form) {
            return;
        }

        let boostAttr = form.getAttribute(boostAttrName);

        if (boostAttr === BoostModes.NONE || !boostAttr) {
            return;
        }

        if (boostAttr === "") {
            boostAttr = BoostModes.PAGE;
        }

        e.preventDefault();
        const method = (form.getAttribute("method") || "GET").toUpperCase();
        const action = form.getAttribute("action") || window.location.href;
        const formData = new FormData(form);

        const methodHasBody = method !== "GET" && method !== "HEAD";
        const url = new URL(action, window.location.href);

        let options: RequestInit = { method };

        if (methodHasBody) {
            options.body = formData;
        } else {
            // append to url
            for (const [key, value] of formData.entries()) {
                if (typeof value === "string") {
                    url.searchParams.append(key, value);
                }
            }
        }

        const boostIncludeSelectors = form.getAttribute(boostIncludeAttrName);
        if (boostIncludeSelectors) {
            const elementsToInclude = document.querySelectorAll(
                boostIncludeSelectors,
            );

            elementsToInclude.forEach((el) => {
                if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
                    const input = el as HTMLInputElement | HTMLTextAreaElement;

                    if (!input.name) {
                        return;
                    }

                    if (
                        input instanceof HTMLInputElement &&
                        (input.type === "checkbox" || input.type === "radio") &&
                        !input.checked
                    ) {
                        return;
                    }

                    if (methodHasBody) {
                        (options.body as FormData).append(
                            input.name,
                            input.value,
                        );
                    } else {
                        url.searchParams.append(input.name, input.value);
                    }
                }
            });
        }

        const finalUrl = url.toString();

        if (boostAttr === BoostModes.PAGE) {
            pageNavigate(finalUrl, options);
            return;
        }

        if (boostAttr === BoostModes.FRAGMENT) {
            fragmentNavigate(form, finalUrl, options);
            return;
        }
    });

    window.addEventListener("popstate", () => {
        // When the user navigates history, fetch the URL but don't push a new state
        pageNavigate(window.location.href, {}, false);
    });

    let intervalId: number;
    window.addEventListener(eventBeforeFetch, () => {
        clearInterval(intervalId);
        intervalId = setInterval(() => {
            document.body.classList.add(requestClassName);
        }, requestInterval);
    });
    window.addEventListener(eventAfterSwap, () => {
        clearInterval(intervalId);
        document.body.classList.remove(requestClassName);
    });
    window.addEventListener(eventError, () => {
        clearInterval(intervalId);
        document.body.classList.remove(requestClassName);
    });
});

export async function fragmentNavigate(
    caller: HTMLElement,
    url: string,
    options?: RequestInit,
) {
    window.dispatchEvent(customEventBeforeFetch(url));

    fetch(url, options)
        .then((res) => {
            if (res.redirected) {
                const redirectUrl = res.url;
                pageNavigate(redirectUrl);
                return;
            }
            if (res.ok) {
                res.text()
                    .then((htmlString) => {
                        fragmentSwap(htmlString, caller);
                        window.dispatchEvent(
                            customEventAfterSwap(BoostModes.FRAGMENT),
                        );
                    })
                    .catch((err) => {
                        window.dispatchEvent(customEventError(err));
                    });
                return;
            }
            res.text()
                .then((htmlString) => {
                    pageSwap(htmlString);
                    window.dispatchEvent(
                        customEventAfterSwap(BoostModes.FRAGMENT),
                    );
                })
                .catch((err) => {
                    window.dispatchEvent(customEventError(err));
                });
        })
        .catch((err) => {
            window.dispatchEvent(customEventError(err));
        });
}

function fragmentSwap(htmlString: string, caller: Element) {
    const template = document.createElement("template");
    template.innerHTML = htmlString;

    const foundPortals = template.content.querySelectorAll("bd-fragment");

    foundPortals.forEach((portal) => {
        const matchingPortals = document.querySelectorAll(
            `bd-fragment[id="${portal.id}"]`,
        );

        if (matchingPortals.length === 0) {
            console.warn(
                `<bd-fragment id="${portal.id}"> found no matching element in document`,
            );
            window.dispatchEvent(customEventMissing(portal));
            return;
        }

        const matchingPortal =
            matchingPortals.length === 1
                ? matchingPortals[0]
                : findClosestElement(caller, Array.from(matchingPortals));

        if (!matchingPortal) {
            console.warn(
                `<bd-fragment id="${portal.id}"> found no matching element in document`,
            );
            window.dispatchEvent(customEventMissing(portal));
            return;
        }

        matchingPortal.outerHTML = portal.outerHTML;
    });
}

function pageNavigate(
    url: string,
    options?: RequestInit,
    pushToHistory = true,
) {
    window.dispatchEvent(customEventBeforeFetch(url));

    let responseUrl: string;
    fetch(url, options)
        .then((res) => {
            // override input url with response url to ensure correct url on redirect
            responseUrl = res.url;
            return res.text();
        })
        .then((htmlString) => {
            pageSwap(htmlString);

            // Update the browser URL without reloading
            if (pushToHistory) {
                window.history.pushState({}, "", responseUrl || url);
            }

            window.dispatchEvent(customEventAfterSwap(BoostModes.PAGE));
        })
        .catch((err) => {
            window.dispatchEvent(customEventError(err));
        });
}

function pageSwap(htmlString: string) {
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(htmlString, "text/html");

    // Swap the title and the body
    mergeHead(newDoc.head);
    document.body.innerHTML = newDoc.body.innerHTML;
}

// taken from htmx-ext-head-support
function mergeHead(newHead: HTMLHeadElement) {
    var removed = [];
    var nodesToAppend = [];

    var currentHead = document.head;

    // put all new head elements into a Map, by their outerHTML
    var srcToNewHeadNodes = new Map<string, Element>();
    for (const newHeadChild of newHead.children) {
        srcToNewHeadNodes.set(newHeadChild.outerHTML, newHeadChild);
    }

    // determine merge strategy

    // get the current head
    for (const currentHeadElt of currentHead.children) {
        // If the current head element is in the map
        var inNewContent = srcToNewHeadNodes.has(currentHeadElt.outerHTML);

        if (inNewContent) {
            // this element already exists and should not be re-appended, so remove it from
            // the new content map, preserving it in the DOM
            srcToNewHeadNodes.delete(currentHeadElt.outerHTML);
        } else {
            removed.push(currentHeadElt);
        }
    }

    // Push the tremaining new head elements in the Map into the
    // nodes to append to the head tag
    nodesToAppend.push(...srcToNewHeadNodes.values());

    for (const newNode of nodesToAppend) {
        var newElt = document
            .createRange()
            .createContextualFragment(newNode.outerHTML);

        currentHead.appendChild(newElt);
    }

    // remove all removed elements, after we have appended the new elements to avoid
    // additional network requests for things like style sheets
    for (const removedElement of removed) {
        currentHead.removeChild(removedElement);
    }
}

function getElementDistance(a: Element, b: Element): number {
    const ancestorsOfA = new Map<Node, number>();
    let node: Node | null = a;
    let depth = 0;
    while (node) {
        ancestorsOfA.set(node, depth);
        depth++;
        node = node.parentNode;
    }

    node = b;
    depth = 0;
    while (node) {
        const depthInA = ancestorsOfA.get(node);
        if (depthInA !== undefined) {
            return depthInA + depth;
        }
        depth++;
        node = node.parentNode;
    }

    // Not connected (e.g. different documents) - treat as unreachable
    return Infinity;
}

function findClosestElement(
    caller: Element,
    candidates: Element[],
): Element | null {
    let closest: Element | null = null;
    let minDistance = Infinity;

    for (const candidate of candidates) {
        const distance = getElementDistance(caller, candidate);
        if (distance < minDistance) {
            minDistance = distance;
            closest = candidate;
        }
    }

    return closest;
}

export function includeElementsInURL(selectors: string, href: string) {
    const url = new URL(href, window.location.href);
    const elementsToInclude = document.querySelectorAll(selectors);

    elementsToInclude.forEach((el) => {
        if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
            const input = el as HTMLInputElement | HTMLTextAreaElement;

            if (!input.name) {
                return;
            }

            if (
                input instanceof HTMLInputElement &&
                (input.type === "checkbox" || input.type === "radio") &&
                !input.checked
            ) {
                return;
            }

            url.searchParams.append(input.name, input.value);
        }
    });

    return url.toString();
}
